/**
 * Safe redirect-target resolution for `returnTo` values (issue #452).
 *
 * `returnTo` arrives from the query string and is used as a redirect target at
 * nine client sinks and two server sinks. Without validation, the OAuth
 * callbacks issued a real 302 to an attacker-chosen host: `getAppRedirectUrl()`
 * returns an origin with no trailing slash, so `${appUrl}${returnTo}` with a
 * `returnTo` of `@evil.example/` produced `https://app.kingprostor.si@evil.example/`
 * — whose actual host is `evil.example`.
 *
 * One implementation serves both sides: the client passes
 * `window.location.origin`, the server passes the resolved `appUrl`.
 *
 * Usage:
 *   safeReturnTo(params.get('returnTo'), window.location.origin)        // client
 *   safeReturnTo(request.cookies.gsx8_oauth_return_to, appUrl)          // server
 *   safeReturnTo(raw, origin, '/map')                                   // custom fallback
 */

/**
 * Resolve a candidate `returnTo` to a safe, site-relative path.
 *
 * Returns the *parsed* value, never the raw candidate, so normalisation the
 * browser would apply later (backslash folding, control-character stripping)
 * cannot change the destination after the check.
 *
 * @param candidate - Untrusted value from a query string or cookie
 * @param baseOrigin - Absolute origin to resolve against and compare with
 * @param fallback - Returned when the candidate is missing or unsafe
 * @returns A site-relative path (`pathname + search + hash`), or `fallback`
 */
export function safeReturnTo(
  candidate: string | null | undefined,
  baseOrigin: string,
  fallback = '/'
): string {
  if (!candidate) return fallback;

  // Length bound (#448 review): a returnTo now also gets duplicated into
  // emailed HTML+text bodies, and some sources (query strings under the
  // ~50 MiB global bodyLimit) are effectively unbounded. No legitimate
  // producer builds paths anywhere near this long.
  if (candidate.length > 2000) return fallback;

  // Every producer in the codebase builds returnTo from location.pathname, so a
  // value that is not site-relative is hostile or malformed. This rejects
  // `https://evil.example`, `javascript:alert(1)`, and the concatenation tricks
  // (`@evil.example/`, `.evil.example/`) that only worked because the old code
  // glued returnTo onto an origin with no trailing slash.
  if (!candidate.startsWith('/')) return fallback;

  let base: URL;
  let url: URL;
  try {
    base = new URL(baseOrigin);
    url = new URL(candidate, base);
  } catch {
    return fallback;
  }

  // Catches what the leading-slash check cannot: WHATWG URL parsing strips ASCII
  // TAB/LF/CR and folds `\` into `/` for special schemes *before* parsing, so
  // `/\evil.example` and `/<TAB>/evil.example` both resolve off-origin despite
  // starting with a slash. `javascript:` and friends yield an opaque origin
  // ("null"), which also fails here.
  if (url.origin !== base.origin) return fallback;

  return url.pathname + url.search + url.hash;
}
